import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { MdFileDownload } from 'react-icons/md';
import DatePicker from 'react-datepicker';
import { isNil } from 'lodash-es';

import colors from 'config/colors';
import { listExportableReports, watchReport } from 'lib/api/reporting';
import * as api from 'lib/api';

import DropDownFilter from 'components/DropDownFilter';
import Page from 'components/Page';
import Button from 'components/Button';
import Loading from 'components/Loading';
import OptionList from 'components/OptionList';
import { Subscription } from 'zen-observable-ts';

import ReportingHeader from '../ReportingHeader';
import 'react-datepicker/dist/react-datepicker.css';
import './Exports.scss';

const customSelectBoxStyles = {
  valueContainer: { fontSize: '14px' },
  menuList: { width: '100%' },
  menu: { width: '100%' },
  option: { fontSize: '14px' },
};

interface BankKey {
  key: string,
  name: string,
}
interface ReportDefinition {
  name: string;
  description: string;
  reportKey: string;
  collabKey: string;
  bankKeys: Array<BankKey> | null;
  parameters: {
    showRangeType: boolean;
    showDateRangePicker: boolean;
  }
}

const CUMULATIVE = 'cumulative';
const DATE_RANGE = 'dateRange';

const styles = {
  button: {
    backgroundColor: colors.positive,
    width: '100%',
  },
  downloadButton: {
    backgroundColor: colors.black,
  },
  icon: {
    display: 'inline-block',
    marginRight: 6,
  },
};

interface ExportsContainerState {
  exportableReports: Array<ReportDefinition> | null;
  loading: boolean;
  error: boolean;
}

class ExportsContainer extends Component<Record<string, never>, ExportsContainerState> {

  constructor(props: Record<string, never>) {
    super(props);
    this.state = {
      exportableReports: null,
      loading: true,
      error: false,
    };
  }

  async componentDidMount(): Promise<void> {
    try {
      const exportableReports = await listExportableReports();
      this.setState({ loading: false, error: false, exportableReports });
    } catch (e) {
      this.setState({ loading: false, error: true });
    }
  }

  render() : JSX.Element {
    const { error, loading, exportableReports } = this.state;

    return (
      <Page vflex title="Reporting" subTitle="Exports">

        <ReportingHeader selectedTabKey="exports" />

        {error || loading || !exportableReports?.length ?
          <div style={{ color: colors.text, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {error && <div style={{ color: colors.red }}>Error loading reports</div>}
            {loading && <Loading size={52} />}
            {!error && !loading && 'No exportable reports available'}
          </div>
          : null
        }

        {!error && !loading && exportableReports?.length ?
          <div className="reportExports scrollable">
            <div className="reportExportWrapper">
              {exportableReports.map((item, index) => <ExportedReportForm zIndex={index} key={item.reportKey} definition={item} adminBanks={this.props.adminBanks} />)}
            </div>
          </div>
          : null
        }
      </Page>
    );
  }
}

interface Service {
  banks: Record<string, unknown>
}

interface GlobalState {
  global : { services: Service[] },
}

function mapStateToProps({ global }: GlobalState) {
  const services = global.services;
  const adminBanks = (services ?? []).flatMap((service) => {
    return Object.keys(service.banks ?? []);
  });

  return {
    adminBanks: [...new Set(adminBanks)],
  };
}


interface ExportedReportFormProps {
  definition: ReportDefinition;
  zIndex: number;
  adminBanks: string[],
}
interface ExportedReportFormState {
  selectedBankKey: string | null;
  dateRangeType: typeof DATE_RANGE | typeof CUMULATIVE | null;
  from: moment.Moment | null;
  to: moment.Moment | null;
  loading: boolean;
  error: string | null;
}

class ExportedReportForm extends Component<ExportedReportFormProps, ExportedReportFormState> {

  private querySubscription : Subscription | null;

  constructor(props: ExportedReportFormProps) {
    super(props);

    const dateRangeType = props.definition.parameters.showDateRangePicker ? DATE_RANGE : null;
    const to = props.definition.parameters.showDateRangePicker ? moment().startOf('isoWeek').subtract(1, 'millisecond') : null;
    const from = props.definition.parameters.showDateRangePicker && to ? to.clone().startOf('isoWeek') : null;

    this.state = {
      selectedBankKey: null,
      dateRangeType,
      from,
      to,
      loading: false,
      error: null,
    };
    this.querySubscription = null;
  }

  componentWillUnmount() {
    if (this.querySubscription) this.querySubscription.unsubscribe();
  }

  reportHasLoaded = ({ error }: { error: string | null }) => {
    this.setState({ loading: false, error });
    if (this.querySubscription) this.querySubscription.unsubscribe();
  }

  // Preserve the local calendar day (avoids BST shifting e.g. Jul 20 00:00+01 → Jul 19 UTC).
  formatLocalCalendarDay = (date: moment.Moment, bound: 'start' | 'end') => {
    const utcDay = moment.utc(date.format('YYYY-MM-DD'));
    return (bound === 'end' ? utcDay.endOf('day') : utcDay.startOf('day')).toISOString();
  };

  downloadReport = async () => {
    // Set loading status to specified report
    this.setState({ loading: true, error: null });

    const reportKey = this.props.definition.reportKey;
    const { from, to, dateRangeType, selectedBankKey } = this.state;
    const range = {
      from: from ? this.formatLocalCalendarDay(from, 'start') : undefined,
      to: to ? this.formatLocalCalendarDay(to, 'end') : undefined,
    };
    if (dateRangeType === CUMULATIVE) {
      range.from = moment.utc('2018-04-30').toISOString();
      range.to = moment.utc().toISOString();
    }

    const response = await api.get('reports/export/queue', { reportKey, reportName: reportKey, from: range.from, to: range.to, bankKey: selectedBankKey });

    const reportError = 'Something went wrong. Please try downloading the report again.';

    // Check if response returned an error
    if (!response.body.success) {
      this.reportHasLoaded({ error: reportError });
    }

    if (response?.body?.success) {
      // Watch for report status to turn 'completed', then download CSV from URL
      this.querySubscription = watchReport({
        reportKey: response.body.key,
        callback: (report: { status: string }) => {
          const status = report?.status;
          if (status === 'completed') {
            window.location.href = response.body.downloadUrl;
            this.reportHasLoaded({ error: null });
          }
          if (status === 'error') this.reportHasLoaded({ error: reportError });
        },
        error: () => {
          // Check if subscription returned an error
          this.reportHasLoaded({ error: reportError });
        },
      });
    }
  }

  onFromDateChange = (date: Date | null) => {
    const from = date ? moment(date).startOf('day') : null;
    const { to } = this.state;
    this.setState({
      from,
      to: from && to && to.isBefore(from) ? from.clone() : to,
    });
  }

  onToDateChange = (date: Date | null) => {
    const to = date ? moment(date).startOf('day') : null;
    const { from } = this.state;
    this.setState({
      to,
      from: to && from && from.isAfter(to) ? to.clone() : from,
    });
  }

  onItemClick = (key: typeof DATE_RANGE | typeof CUMULATIVE) => {
    this.setState({ dateRangeType: key });
  }

  onBankKeyChange = (key: string) => {
    this.setState({ selectedBankKey: key });
  }

  render() {
    const { definition, zIndex } = this.props;
    const { dateRangeType, loading, error, from, to, selectedBankKey } = this.state;

    const bankOptions = definition?.bankKeys?.filter(bank => this.props.adminBanks.includes(bank.key)).map(({ name, key }) => {
      return { value: key, label: name };
    }) ?? [];

    if (bankOptions.length && isNil(selectedBankKey)) this.onBankKeyChange(bankOptions[0].value); // set default options

    return (
      <div className="reportExport" style={{ zIndex: (999 - zIndex) }} key={definition.reportKey}>
        <h2>{definition.name}</h2>
        <div className="reportControls">
          {definition.description ? <p className="report-explanation" style={{ marginLeft: 0 }}>{definition.description}</p> : null}
          <div className="reportControlsFooter">
            {(definition.bankKeys && definition.bankKeys.length) ? (
              <DropDownFilter
                title="Your available staff bank:"
                placeholder=""
                options={bankOptions}
                value={bankOptions.find(option => option.value === selectedBankKey)}
                onChange={(obj) => this.onBankKeyChange(obj.value)}
                customStyles={customSelectBoxStyles}
              />
            ) : null
            }
            {(definition.parameters.showRangeType || definition.parameters.showDateRangePicker) ? (
              <div className="reportRangeRow">
                {definition.parameters.showRangeType ?
                  <div className="picker">
                    <OptionList
                      items={[
                        {
                          key: DATE_RANGE,
                          name: 'Date Range',
                          selected: dateRangeType === DATE_RANGE,
                        },
                        {
                          key: CUMULATIVE,
                          name: 'Cumulative',
                          selected: dateRangeType === CUMULATIVE,
                        },
                      ]}
                      onItemClick={this.onItemClick}
                    />
                  </div> : null
                }
                {definition.parameters.showDateRangePicker ?
                  <div className={`reportDateFieldGroup${dateRangeType === CUMULATIVE ? ' reportDateFieldGroup--disabled' : ''}`} role="group" aria-label="Report date range">
                    <div className="reportDateField">
                      <label className="reportDateFieldLabel" htmlFor={`report-from-${definition.reportKey}`}>From</label>
                      <DatePicker
                        id={`report-from-${definition.reportKey}`}
                        selected={from?.toDate() ?? null}
                        onChange={this.onFromDateChange}
                        selectsStart
                        startDate={from?.toDate() ?? null}
                        endDate={to?.toDate() ?? null}
                        maxDate={to?.toDate() ?? undefined}
                        disabled={loading || dateRangeType === CUMULATIVE}
                        dateFormat="MMM d, yyyy"
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        scrollableYearDropdown
                        yearDropdownItemNumber={30}
                        calendarStartDay={1}
                        placeholderText="Start date"
                        calendarClassName="reportDatePickerCalendar"
                        popperClassName="reportDatePickerPopper"
                        wrapperClassName="reportDateFieldInput"
                        portalId="report-exports-datepicker-portal"
                        popperProps={{ strategy: 'fixed' }}
                      />
                    </div>
                    <div className="reportDateField">
                      <label className="reportDateFieldLabel" htmlFor={`report-to-${definition.reportKey}`}>To</label>
                      <DatePicker
                        id={`report-to-${definition.reportKey}`}
                        selected={to?.toDate() ?? null}
                        onChange={this.onToDateChange}
                        selectsEnd
                        startDate={from?.toDate() ?? null}
                        endDate={to?.toDate() ?? null}
                        minDate={from?.toDate() ?? undefined}
                        disabled={loading || dateRangeType === CUMULATIVE}
                        dateFormat="MMM d, yyyy"
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        scrollableYearDropdown
                        yearDropdownItemNumber={30}
                        calendarStartDay={1}
                        placeholderText="End date"
                        calendarClassName="reportDatePickerCalendar"
                        popperClassName="reportDatePickerPopper"
                        wrapperClassName="reportDateFieldInput"
                        portalId="report-exports-datepicker-portal"
                        popperProps={{ strategy: 'fixed' }}
                      />
                    </div>
                  </div>
                  : null
                }
              </div>
            ) : null}
            <div className="reportButtons">
              <Button
                disabled={loading}
                white
                outline
                shadow={false}
                className="reportDownloadButton"
                icon={loading ? null : <MdFileDownload style={styles.icon} />}
                onClick={() => this.downloadReport()}
              >
                {loading ? 'Downloading...' : 'Download'}
              </Button>
            </div>
            {error && <div style={{ color: colors.red, paddingTop: 12 }}>{error}</div>}
          </div>
        </div>
      </div>
    );
  }
}

export default connect(mapStateToProps)(ExportsContainer);
